import { Plus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import SearchBar from '@/features/documents/components/search-bar';
import { useCanManageOrgMembers } from '@/features/org/hooks/use-can-manage-org-members';
import { ProjectMemberManagementTable } from '@/features/projects/shared/components/project-member-management/project-member-management-table';
import { ProjectMemberPickerDialog } from '@/features/projects/shared/components/project-member-management/project-member-picker-dialog';
import { useDeleteProjectMemberInviteMutation } from '@/features/projects/shared/hooks/use-delete-project-member-invite-mutation';
import { useDeleteProjectMemberMutation } from '@/features/projects/shared/hooks/use-delete-project-member-mutation';
import { useInviteProjectMembersMutation } from '@/features/projects/shared/hooks/use-invite-project-members-mutation';
import { useResendProjectMemberInviteMutation } from '@/features/projects/shared/hooks/use-resend-project-member-invite-mutation';
import { ProjectMemberManagementData } from '@/features/projects/shared/types/project-member-management-types';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { Button } from '@/shared/components/ui/button';
import { useProjectHubFilterStore } from '@/shared/hooks/use-hub-filter-store';
import { toast } from '@/shared/hooks/use-toast';
import { useErrorTranslation } from '@/shared/utils/error-handling';

export const ProjectMemberManagementPage = () => {
  const { t } = useTranslation('project', { keyPrefix: 'MemberManagement' });
  const { projectId } = useParams<{ projectId: string }>();
  const canManageOrgMembers = useCanManageOrgMembers();
  const navigate = useNavigate();
  const errorTranslation = useErrorTranslation();
  const { openPaywallFromError } = usePaywall();

  useEffect(() => {
    if (!projectId) {
      navigate(paths.projects.hub.getHref());
    }
  }, [projectId]);

  useEffect(() => {
    if (!canManageOrgMembers) {
      navigate(paths.projects.details.base.getHref(projectId!));
    }
  }, [canManageOrgMembers, projectId]);

  const { searchQuery, setSearchQuery } = useProjectHubFilterStore();
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);

  const deleteMemberMutation = useDeleteProjectMemberMutation({
    onSuccess: () => {
      toast({ title: t('messages.delete_success') });
    },
    onError: () => {
      toast({
        title: t('messages.delete_error'),
        variant: 'destructive',
      });
    },
  });

  const inviteMembersMutation = useInviteProjectMembersMutation({
    onSuccess: () => {
      toast({ title: t('messages.add_success') });
      setAddMemberDialogOpen(false);
    },
    onError: async (error: unknown) => {
      const handled = await openPaywallFromError(error, Number(projectId));
      if (!handled) {
        const errorMessage = errorTranslation(error);
        toast({
          title: errorMessage,
          variant: 'destructive',
        });
      }
    },
  });

  const resendInviteMutation = useResendProjectMemberInviteMutation({
    onSuccess: () => toast({ title: t('invitation_resent') }),
    onError: () =>
      toast({
        title: t('invitation_resend_error'),
        variant: 'destructive',
      }),
  });

  const deleteInviteMutation = useDeleteProjectMemberInviteMutation({
    onSuccess: () => {
      toast({ title: t('messages.delete_success') });
    },
    onError: () => {
      toast({
        title: t('messages.delete_error'),
        variant: 'destructive',
      });
    },
  });

  const handleResendInvitation = (inviteId: number) => {
    resendInviteMutation.mutate({ inviteId, projectId });
  };

  const handleDeleteInvitation = (inviteId: number) => {
    deleteInviteMutation.mutate({ inviteId, projectId });
  };

  const handleDeleteMember = (member: ProjectMemberManagementData) => {
    // Only allow deletion of regular members, not owners/admins since they're tied to org
    if (member.type === 'org_member') {
      toast({
        title: t('messages.cannot_delete_org_member'),
        variant: 'destructive',
      });
      return;
    }

    if (projectId && member.type === 'member') {
      deleteMemberMutation.mutate({
        projectId: parseInt(projectId),
        orgMemberId: member.projectMember.org_member_id,
      });
    }
  };

  if (!projectId) {
    return null;
  }

  return (
    <DefaultLayout
      title={t('title')}
      headerActions={[
        <SearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('search_placeholder')}
          className="w-36"
        />,
        <Button
          btnType={'secondary'}
          variant={'greenGradient'}
          onClick={() => setAddMemberDialogOpen(true)}
        >
          <Plus className="mr-1 h-4 w-4" />
          {t('add_member_button')}
        </Button>,
      ]}
    >
      <ProjectMemberManagementTable
        projectId={projectId}
        handleDeleteMember={handleDeleteMember}
        onResendInvitation={handleResendInvitation}
        onDeleteInvitation={handleDeleteInvitation}
      />

      <ProjectMemberPickerDialog
        projectId={projectId}
        open={addMemberDialogOpen}
        onOpenChange={setAddMemberDialogOpen}
        isLoading={inviteMembersMutation.isPending}
        onSendInvitations={(newInvites) => {
          inviteMembersMutation.mutate({
            projectId: parseInt(projectId),
            orgMemberIds: Array.from(newInvites),
          });
        }}
      />
    </DefaultLayout>
  );
};
